import { type VariantProps, cva } from "class-variance-authority";
import type * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Komunikat.
 *
 * Rola alertu tylko dla wariantów wymagających uwagi. Nadana każdemu
 * komunikatowi sprawiałaby, że czytnik ekranu przerywa czytanie także przy
 * neutralnej informacji — a wtedy ludzie go wyłączają.
 */
const alertVariants = cva(
  cn(
    "flex w-full items-start gap-3 rounded-lg border px-4 py-3 text-sm",
    "[&>svg]:mt-0.5 [&>svg]:size-4 [&>svg]:shrink-0",
  ),
  {
    variants: {
      variant: {
        default: "border-border bg-card text-card-foreground",
        info: "border-accent/40 bg-accent/10 text-accent",
        success: "border-success/40 bg-success/10 text-success",
        warning: "border-warning/40 bg-warning/10 text-warning",
        destructive: "border-destructive/40 bg-destructive/10 text-destructive",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface AlertProps
  extends React.ComponentProps<"div">,
    VariantProps<typeof alertVariants> {}

export function Alert({ className, variant, ...props }: AlertProps) {
  const assertive = variant === "destructive" || variant === "warning";
  return (
    <div
      data-slot="alert"
      {...(assertive ? { role: "alert" } : {})}
      className={cn(alertVariants({ variant }), className)}
      {...props}
    />
  );
}

export function AlertTitle({ className, ...props }: React.ComponentProps<"p">) {
  return <p data-slot="alert-title" className={cn("font-medium", className)} {...props} />;
}

export function AlertDescription({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div data-slot="alert-description" className={cn("text-sm opacity-90", className)} {...props} />
  );
}
